import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { createTheme, ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext({
  toggleDarkMode: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(localStorage.getItem('dark-theme') === 'true' ? 'dark' : 'light');

  const colorMode = useMemo(
    () => ({
      toggleDarkMode: () => {
        const newMode = mode === 'light' ? 'dark' : 'light';
        setMode(newMode);
        localStorage.setItem('dark-theme', newMode === 'dark');
      },
    }),
    [mode]
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === 'light'
            ? {
                primary: { main: '#0A4FAC', switch: '#f2f2f2', white: '#f2f2f2', border: '#e0e0e0' },
                secondary: { main: '#F0F0F0' },
                background: {
                  default: '#fff',
                  paper: '#FFFFFF',
                  card: '#0A4FAC',
                  secondary: '#0A4FAC',
                  blur: 'rgba(255,255,255,0.5)',
                },
                text: { primary: '#000', secondary: '#0A4FAC', wb: 'white' },
                action: { active: '#0A4FAC', hover: '#E0E0E0', disabledBackground: '#ccc', disabled: '#999' },
                chart: { red: ['red'] },
              }
            : {
                primary: { main: '#0A4FAC', switch: '#071C21', border: '#333333' },
                secondary: { main: '#808080' },
                background: {
                  default: 'rgb(24, 26, 27)',
                  paper: 'rgb(24, 26, 27)',
                  card: '#0A4FAC',
                  secondary: '#0A4FAC',
                  blur: 'rgba(0,0,0,0.5)',
                },
                text: { primary: '#fff', secondary: 'rgb(136, 199, 255)', card: '#999999', wb: 'white' },
                action: { active: '#0A4FAC', hover: '#999999', disabledBackground: '#ccc', disabled: '#999' },
              }),
        },
        typography: {
          fontFamily: 'Regular',
        },
        components: {
          MuiTableCell: {
            styleOverrides: {
              footer: {
                p: 0,
                border: 0,
              },
            },
          },
          MUIDataTableToolbar: {
            styleOverrides: {
              root: {
                display: 'flex',
              },
            },
          },
          // MuiSvgIcon: {
          //   styleOverrides: {
          //     root: {
          //       color: mode === 'dark' ? '#ffffff' : '#000000',
          //     },
          //   },
          // },
          calendarIcon: {
            styleOverrides: {
              root: {
                color: mode === 'dark' ? '#ffffff' : '#000000',
              },
            },
          },
          MuiMenu: {
            styleOverrides: {
              arrow: {
                color: mode === 'dark' ? '#ffffff' : '#000000',
              },
            },
          },
          MuiInputAdornment: {
            styleOverrides: {
              root: {
                '& .MuiSvgIcon-root': {
                  color: mode === 'dark' ? '#ffffff' : '#000000',
                },
              },
            },
          },
          MuiAutocomplete: {
            styleOverrides: {
              '& .MuiFormLabel-root': {
                color: mode === 'dark' ? '#ffffff' : '#000000',
                '&.Mui-focused': {
                  color: mode === 'dark' ? '#71c9ff' : '#0A4FAC',
                },
              },
            },
          },
          MuiFormControl: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'dark' ? '#71c9ff' : '#0A4FAC',
                },
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                color: mode === 'dark' ? '#ffffff' : '#000000',
                '&.Mui-focused': {
                  color: mode === 'dark' ? '#71c9ff' : '#0A4FAC',
                },
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              // Existing styles...
              // '& .MuiFormLabel-root': {
              //   color: mode === 'dark' ? '#ffffff' : '#000000', // Default label color
              //   '&.Mui-focused': {
              //     color: mode === 'dark' ? '#71c9ff' : '#0A4FAC', // Label color when focused
              //     transform: 'translateY(-10px)', // Move label upwards
              //   },
              // },
              select: {
                '&:focus': {
                  backgroundColor: 'transparent',
                },
              },
              icon: {
                color: mode === 'dark' ? '#ffffff' : '#000000',
                '&.Mui-focused': {
                  color: mode === 'dark' ? '#71c9ff' : '#0A4FAC', // Icon color when focused
                },
              },
              '& .MuiSelect-icon': {
                color: mode === 'dark' ? '#ffffff' : '#000000',
                '&.Mui-focused': {
                  color: mode === 'dark' ? '#71c9ff' : '#0A4FAC', // Icon color when focused
                },
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '&& input[type="date"]': {
                  '&::-webkit-calendar-picker-indicator': {
                    color: mode === 'dark' ? '#ffffff' : '#000000',
                    cursor: 'poiner',
                    filter: mode === 'dark' ? 'invert(1)' : 'invert(0)',
                  },
                },
                '& .MuiFormLabel-root': {
                  color: mode === 'dark' ? '#ffffff' : '#000000',
                  '&.Mui-focused': {
                    color: mode === 'dark' ? '#71c9ff' : '#0A4FAC',
                  },
                },

                '& .MuiInputAdorment-root .MuiSvgIcon-root': {
                  color: mode === 'dark' ? '#ffffff' : '#000000',
                },
                '& .MuiOutlinedInput-root': {
                  //   '& fieldset': {
                  //     borderColor: mode === 'light' ? '#0A4FAC' : '#71c9ff', // Border color of the field
                  //   },
                  // '&:hover fieldset': {
                  //   borderColor: mode === 'light' ? '#0A4FAC' : '#71c9ff', // Border color on hover
                  // },
                  '&.Mui-focused fieldset': {
                    borderColor: mode === 'light' ? '#0A4FAC' : '#ffffff', // Border color when focused
                  },
                },
              },
            },
          },
          MuiListItemButton: {
            styleOverrides: {
              root: {
                '&.Mui-selected, &.Mui-selected:hover': {
                  backgroundColor: 'rgba(0, 49, 113, 0.7)',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
                '&:hover': {
                  backgroundColor: '#0A4FAC',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ colorMode, mode }}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
